import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { Button, Field, FieldError, FieldLabel, Input, Modal } from '@/components/ui';
import { formatTimeHHMM } from '@/utils/timeFormat';
import type { ScheduleOccurrence } from '@/utils/scheduleOccurrences';

function localYmdAndTimeToDate(ymd: string, time: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

function formatDateDisplay(ymd: string): string {
  const date = localYmdAndTimeToDate(ymd, '12:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export interface PlayEventRescheduleModalProps {
  isOpen: boolean;
  occurrence: ScheduleOccurrence | null;
  eventName: string;
  isLoading?: boolean;
  stackLevel?: 'default' | 'elevated';
  onCancel: () => void;
  onConfirm: (startsAt: Date, endsAt: Date) => Promise<void>;
}

export function PlayEventRescheduleModal({
  isOpen,
  occurrence,
  eventName,
  isLoading = false,
  stackLevel = 'elevated',
  onCancel,
  onConfirm,
}: PlayEventRescheduleModalProps) {
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !occurrence) return;
    setNewDate(format(occurrence.date, 'yyyy-MM-dd'));
    setNewStartTime(occurrence.startTime);
    setNewEndTime(occurrence.endTime);
    setStep('form');
    setError('');
  }, [isOpen, occurrence]);

  const handleProceedToConfirm = () => {
    if (!newDate) {
      setError('Please select a new date');
      return;
    }
    if (!newStartTime || !newEndTime) {
      setError('Please provide start and end times');
      return;
    }
    const startsAt = localYmdAndTimeToDate(newDate, newStartTime);
    const endsAt = localYmdAndTimeToDate(newDate, newEndTime);
    if (endsAt <= startsAt) {
      setError('End time must be after start time');
      return;
    }
    setError('');
    setStep('confirm');
  };

  const handleConfirmSubmit = async () => {
    if (!newDate || !newStartTime || !newEndTime) return;
    try {
      setSubmitting(true);
      setError('');
      const startsAt = localYmdAndTimeToDate(newDate, newStartTime);
      const endsAt = localYmdAndTimeToDate(newDate, newEndTime);
      await onConfirm(startsAt, endsAt);
      setStep('form');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reschedule event';
      if (message.toLowerCase().includes('court schedule conflict')) {
        setError(
          'Those courts are already booked for the selected time. Choose a different time or release the conflicting booking first.'
        );
      } else {
        setError(message);
      }
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting || isLoading) return;
    setStep('form');
    setError('');
    onCancel();
  };

  if (!isOpen || !occurrence) return null;

  const originalDateLabel = format(occurrence.date, 'EEEE, MMMM d, yyyy');
  const originalTimeLabel = `${formatTimeHHMM(occurrence.startTime)} – ${formatTimeHHMM(occurrence.endTime)}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reschedule event"
      maxWidth="sm"
      stackLevel={stackLevel}
    >
      {step === 'form' ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{eventName}</p>
            <p>Currently {originalDateLabel}</p>
            <p className="tabular-nums">{originalTimeLabel}</p>
          </div>

          <Field>
            <FieldLabel htmlFor="play-event-reschedule-date">New date</FieldLabel>
            <Input
              id="play-event-reschedule-date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              disabled={submitting || isLoading}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="play-event-reschedule-start">Start time</FieldLabel>
              <Input
                id="play-event-reschedule-start"
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                disabled={submitting || isLoading}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-event-reschedule-end">End time</FieldLabel>
              <Input
                id="play-event-reschedule-end"
                type="time"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                disabled={submitting || isLoading}
              />
            </Field>
          </div>

          {error ? <FieldError>{error}</FieldError> : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting || isLoading}>
              Cancel
            </Button>
            <Button type="button" onClick={handleProceedToConfirm} disabled={submitting || isLoading}>
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div>
              <p className="font-medium">Confirm reschedule</p>
              <p className="mt-1">
                Move <strong>{eventName}</strong> from {originalDateLabel} to{' '}
                <strong>{formatDateDisplay(newDate)}</strong> ({formatTimeHHMM(newStartTime)} –{' '}
                {formatTimeHHMM(newEndTime)}).
              </p>
              <p className="mt-2 text-xs">
                Court holds will update to the new time. Participants are not notified automatically in this
                version.
              </p>
            </div>
          </div>

          {error ? <FieldError>{error}</FieldError> : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('form')}
              disabled={submitting || isLoading}
            >
              Back
            </Button>
            <Button type="button" onClick={() => void handleConfirmSubmit()} disabled={submitting || isLoading}>
              {submitting || isLoading ? 'Saving…' : 'Reschedule'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
